/**
 * SemanticDatePicker Component
 *
 * A custom date picker that handles:
 * - Semantic NULL values (Effective Immediately, No End Date, etc.)
 * - Quick presets (Next Sunday, +30 days, End of Quarter, etc.)
 * - Confirm-before-close (user must click Apply to commit)
 * - Clear visual states (always know what you're about to commit)
 *
 * This component replaces the problematic Ant Design DatePicker with
 * fully controlled state management and predictable behavior.
 */

import React, { useRef, useEffect, useMemo } from 'react'
import { Dropdown } from 'antd'
import type { SemanticDatePickerProps, DatePreset } from './types'
import { useSemanticDateState } from './hooks/useSemanticDateState'
import { getSemanticConfig } from './utils/semantics'
import { DEFAULT_PRESETS, createDefaultPresets } from './utils/presets'
import { getTimezoneAbbreviation, getBrowserTimezone } from './utils/timezone'
import { DateDisplay } from './components/DateDisplay'
import { DatePanel } from './components/DatePanel'
import './SemanticDatePicker.css'

/**
 * SemanticDatePicker - A date picker with semantic NULL support.
 *
 * Key features:
 * 1. **Semantic NULL**: NULL can mean "Effective Immediately", "No End Date", etc.
 * 2. **Presets**: Quick buttons for common dates (Next Sunday, +30 days, etc.)
 * 3. **Confirm-before-close**: All changes require explicit Apply
 * 4. **Clear visual states**: Always know what you're selecting
 *
 * @example
 * // Basic usage
 * <SemanticDatePicker
 *   value={startDate}
 *   onChange={setStartDate}
 *   dateSemantics="on_activation"
 * />
 *
 * @example
 * // End date with "No End Date" semantic
 * <SemanticDatePicker
 *   value={endDate}
 *   onChange={setEndDate}
 *   dateSemantics="end_of_time"
 * />
 *
 * @example
 * // With custom presets
 * <SemanticDatePicker
 *   value={date}
 *   onChange={setDate}
 *   presets={[
 *     { key: 'today', label: 'Today', getValue: () => dayjs() },
 *     { key: 'tomorrow', label: 'Tomorrow', getValue: () => dayjs().add(1, 'day') },
 *   ]}
 * />
 *
 * @example
 * // Without presets
 * <SemanticDatePicker
 *   value={date}
 *   onChange={setDate}
 *   presets={false}
 * />
 */
export const SemanticDatePicker: React.FC<SemanticDatePickerProps> = ({
  value = null,
  onChange,
  dateSemantics,
  allowClear = true,
  format = 'MMM D, YYYY',
  showTime = false,
  timeFormat = 'h:mm A',
  presets: customPresets,
  disabled = false,
  placeholder = 'Select date',
  onOpenChange,
  timezone,
  minDate,
  maxDate,
  disabledDateMessage,
  style,
  className,
  width = '100%',
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  // Get semantic config for NULL display
  const semanticConfig = useMemo(() => {
    if (!allowClear) return null
    return getSemanticConfig(dateSemantics)
  }, [dateSemantics, allowClear])

  // Calculate timezone abbreviation for display
  const timezoneAbbreviation = useMemo(() => {
    const effectiveTimezone = timezone || getBrowserTimezone()
    // Use current date to get correct DST abbreviation
    return getTimezoneAbbreviation(effectiveTimezone, new Date())
  }, [timezone])

  // Determine which presets to use
  // When timezone is provided, use timezone-aware preset calculations
  const presets: DatePreset[] | false = useMemo(() => {
    if (customPresets === false) return false
    if (customPresets && customPresets.length > 0) return customPresets
    // Use timezone-aware presets when org timezone is provided
    if (timezone) return createDefaultPresets(timezone)
    return DEFAULT_PRESETS
  }, [customPresets, timezone])

  // State management
  const { state, actions } = useSemanticDateState({
    value,
    onChange,
    onOpenChange,
  })

  // Handle click outside to close
  useEffect(() => {
    if (!state.isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside our container and ANY dropdown overlay
      const target = event.target as Node
      const container = containerRef.current

      // Don't close if clicking inside our container
      if (container?.contains(target)) return

      // Don't close if clicking inside ANY dropdown overlay (handles multiple pickers)
      // Include Ant Design Select and Picker dropdowns which portal to document.body
      const dropdownOverlays = document.querySelectorAll(
        '.semantic-date-dropdown, .ant-select-dropdown, .ant-picker-dropdown'
      )
      for (const overlay of dropdownOverlays) {
        if (overlay.contains(target)) return
      }

      // Otherwise close (cancel without applying)
      actions.cancel()
    }

    // Use setTimeout to avoid immediate close on open click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [state.isOpen, actions])

  // Handle escape key to close
  useEffect(() => {
    if (!state.isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        actions.cancel()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [state.isOpen, actions])

  // Render the dropdown content (panel)
  const dropdownContent = (
    <DatePanel
      committedValue={value}
      pendingValue={state.pendingValue}
      wantsSemanticNull={state.wantsSemanticNull}
      hasChange={state.hasChange}
      semanticConfig={semanticConfig}
      presets={presets}
      showTime={showTime}
      minDate={minDate}
      maxDate={maxDate}
      disabledDateMessage={disabledDateMessage}
      onSelectDate={actions.selectDate}
      onSelectSemanticNull={actions.selectSemanticNull}
      onApply={actions.apply}
      onCancel={actions.cancel}
    />
  )

  // Build container style
  const containerStyle: React.CSSProperties = {
    width,
    ...style,
  }

  return (
    <div
      ref={containerRef}
      className={`semantic-date-picker ${className || ''} ${disabled ? 'semantic-date-picker--disabled' : ''}`}
      style={containerStyle}
    >
      <Dropdown
        open={state.isOpen && !disabled}
        trigger={[]}
        placement="bottomLeft"
        overlayClassName="semantic-date-dropdown"
        popupRender={() => dropdownContent}
      >
        <div>
          <DateDisplay
            value={value}
            pendingValue={state.pendingValue}
            wantsSemanticNull={state.wantsSemanticNull}
            semanticConfig={semanticConfig}
            format={format}
            timeFormat={timeFormat}
            showTime={showTime}
            disabled={disabled}
            isOpen={state.isOpen}
            placeholder={placeholder}
            timezoneAbbreviation={timezoneAbbreviation}
            onClick={actions.toggle}
          />
        </div>
      </Dropdown>
    </div>
  )
}

export default SemanticDatePicker
